import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_KEY_SCOPES, type ApiKeyScope } from '@frankenstein/shared';
import { useGuildConfig } from '../../../lib/auditQueries.js';
import { useApiKeys, useApiKeyMutations, type MintedApiKey } from '../../../lib/apiKeysQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';

export const Route = createFileRoute('/guilds/$guildId/api-keys')({
  component: ApiKeysPage,
});

function ApiKeysPage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const list = useApiKeys(guildId);
  const m = useApiKeyMutations(guildId);
  const [creating, setCreating] = useState(false);
  const [minted, setMinted] = useState<MintedApiKey | null>(null);

  const rows = list.data ?? [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-4 flex items-center justify-between">
          <p className="flex items-center gap-2 text-sm text-zinc-400">
            {t('apiKeys.subtitle')}
            <span className="rounded-full border border-violet-700/60 bg-violet-950/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-300">
              Business
            </span>
          </p>
          <button
            type="button"
            onClick={() => {
              setCreating(true);
            }}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
          >
            {t('apiKeys.new')}
          </button>
        </div>

        <p className="mb-5 rounded-md border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400">
          {t('apiKeys.mcpHint')} <code className="font-mono text-zinc-300">/api/mcp</code>
        </p>

        {minted ? (
          <div className="mb-5 rounded-lg border border-emerald-800 bg-emerald-950/30 p-4">
            <p className="mb-2 text-sm font-medium text-emerald-300">{t('apiKeys.mintedTitle')}</p>
            <code className="block break-all rounded bg-zinc-950 px-3 py-2 font-mono text-xs text-emerald-200">
              {minted.key}
            </code>
            <p className="mt-2 text-xs text-zinc-400">{t('apiKeys.mintedHint')}</p>
            <button
              type="button"
              onClick={() => {
                setMinted(null);
              }}
              className="mt-2 text-xs text-zinc-400 hover:text-zinc-200"
            >
              {t('common.close', { defaultValue: 'Close' })}
            </button>
          </div>
        ) : null}

        {list.isLoading ? (
          <p className="text-sm text-zinc-400">{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-500">
            {t('apiKeys.empty')}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-4 py-2">{t('apiKeys.col.name')}</th>
                  <th className="px-4 py-2">{t('apiKeys.col.scopes')}</th>
                  <th className="px-4 py-2">{t('apiKeys.col.lastUsed')}</th>
                  <th className="px-4 py-2 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {rows.map((k) => (
                  <tr key={k.id} className={k.revokedAt ? 'opacity-40' : 'hover:bg-zinc-900/40'}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{k.name}</div>
                      <code className="font-mono text-xs text-zinc-500">{k.prefix}…</code>
                    </td>
                    <td className="px-4 py-2 text-xs text-zinc-400">{k.scopes.join(', ')}</td>
                    <td className="px-4 py-2 text-xs text-zinc-500">
                      {k.revokedAt
                        ? t('apiKeys.revoked')
                        : k.lastUsedAt
                          ? new Date(k.lastUsedAt).toLocaleDateString()
                          : t('apiKeys.never')}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {!k.revokedAt ? (
                        <button
                          type="button"
                          disabled={m.revoke.isPending}
                          onClick={() => {
                            m.revoke.mutate(k.id);
                          }}
                          className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-50"
                        >
                          {t('apiKeys.revoke_action')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {creating ? (
        <CreateModal
          onClose={() => {
            setCreating(false);
          }}
          onCreate={(name, scopes) => {
            m.create.mutate(
              { name, scopes },
              {
                onSuccess: (k) => {
                  setMinted(k);
                  setCreating(false);
                },
              },
            );
          }}
          pending={m.create.isPending}
        />
      ) : null}
    </div>
  );
}

function CreateModal({
  onClose,
  onCreate,
  pending,
}: {
  onClose: () => void;
  onCreate: (name: string, scopes: ApiKeyScope[]) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>([]);
  const toggle = (s: ApiKeyScope): void => {
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };
  const valid = name.trim() !== '' && scopes.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-950 p-6">
        <h2 className="mb-4 text-lg font-semibold">{t('apiKeys.create.title')}</h2>
        <label className="mb-1 block text-xs text-zinc-400">{t('apiKeys.create.name')}</label>
        <input
          type="text"
          value={name}
          maxLength={80}
          onChange={(e) => {
            setName(e.target.value);
          }}
          className="mb-4 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm"
        />
        <p className="mb-2 text-xs text-zinc-400">{t('apiKeys.create.scopes')}</p>
        <div className="mb-5 space-y-1.5">
          {API_KEY_SCOPES.map((s) => (
            <label key={s} className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggle(s)} />
              <code className="font-mono text-xs">{s}</code>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!valid || pending}
            onClick={() => {
              onCreate(name, scopes);
            }}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {t('apiKeys.create.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const config = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            { label: config.data?.name ?? guildId, to: '/guilds/$guildId', params: { guildId } },
            { label: t('apiKeys.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}
